/**
 * The backup switch: a translator nobody is using goes to sleep.
 *
 * The organiser ending the call is the main way a session stops. This is for
 * everything that way misses — a tab left open after the goodbye, a peer who
 * walked off without leaving, a call that simply went quiet. The provider
 * bills a session for as long as it is open, silence included (`playout.ts`
 * found it sends continuous audio), and only this browser can hang it up.
 *
 * Two halves, deliberately separate:
 *
 * - **Falling asleep** is decided on transcript deltas. A delta of either kind
 *   is proof somebody was understood, and the provider sends none in silence,
 *   so a minute without one is a minute of paying for nothing. No audio
 *   analysis is needed to know that.
 * - **Waking up** cannot use the provider — its session is exactly what was
 *   closed. So the peer's original track is listened to locally, and only for
 *   loudness: nothing is recorded, nothing leaves the browser, and the check
 *   runs only while asleep.
 *
 * What it costs: the first moment of speech after a sleep arrives while a new
 * session is still connecting, and is not translated. A second or two, once,
 * after a minute nobody spoke — against a session that would otherwise bill
 * until somebody noticed.
 */

/** Quiet for this long and the session is closed. The figure asked for. */
export const IDLE_MS = 60_000;

/** Calls `onIdle` once nothing has happened for `idleMs`. */
export class IdleSwitch {
  #idleMs: number;
  #onIdle: () => void;
  #timer: ReturnType<typeof setTimeout> | null = null;

  constructor(onIdle: () => void, idleMs = IDLE_MS) {
    this.#onIdle = onIdle;
    this.#idleMs = idleMs;
  }

  /** Something was said: the minute starts again. Also what arms it. */
  activity(): void {
    this.stop();
    this.#timer = setTimeout(() => {
      this.#timer = null;
      this.#onIdle();
    }, this.#idleMs);
  }

  stop(): void {
    if (this.#timer !== null) clearTimeout(this.#timer);
    this.#timer = null;
  }

  get armed(): boolean {
    return this.#timer !== null;
  }
}

/**
 * RMS of a time-domain frame, 0..1, above which a frame counts as voice.
 *
 * About -34 dBFS. The peer's browser runs noise suppression and gain control
 * before sending, so a quiet room arrives well below this and speech well
 * above it. Set too low, a fan wakes the translator every minute and each wake
 * bills a minute; set too high, a soft voice goes untranslated until someone
 * presses the button.
 */
const VOICE_LEVEL = 0.02;
/** How many loud frames in a row. A cough or a door is shorter than this. */
const VOICE_FRAMES = 5;
/** Frame spacing. 5 × 50 ms: a quarter second of sound before waking. */
const FRAME_MS = 50;

/** The decision alone, so it can be tested without a sound card. */
export class SpeechGate {
  #level: number;
  #frames: number;
  #run = 0;

  constructor(level = VOICE_LEVEL, frames = VOICE_FRAMES) {
    this.#level = level;
    this.#frames = frames;
  }

  /** One frame's RMS. True on the frame that completes a run of voice. */
  push(rms: number): boolean {
    this.#run = rms >= this.#level ? this.#run + 1 : 0;
    return this.#run >= this.#frames;
  }
}

export function rms(samples: Float32Array): number {
  if (samples.length === 0) return 0;
  let sum = 0;
  for (const s of samples) sum += s * s;
  return Math.sqrt(sum / samples.length);
}

/**
 * Listens to `track` until it hears voice, calls `onVoice` once, and stops.
 * Returns the way to stop it sooner. Never throws: a browser that cannot do
 * this leaves the manual button, which is why the button exists.
 *
 * The track is the call's, not ours, and is not stopped here — only the
 * audio graph built on it is torn down.
 */
export function listenForVoice(track: MediaStreamTrack, onVoice: () => void): () => void {
  let ctx: AudioContext;
  try {
    ctx = new AudioContext();
  } catch {
    return () => undefined;
  }

  const source = ctx.createMediaStreamSource(new MediaStream([track]));
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 1024;
  source.connect(analyser);
  // A context made without a fresh click may start suspended, and a suspended
  // one reads silence forever. The call screen was reached by clicking, which
  // is usually enough; when it is not, this simply never fires.
  void ctx.resume().catch(() => undefined);

  const gate = new SpeechGate();
  const frame = new Float32Array(analyser.fftSize);
  let stopped = false;

  const stop = () => {
    if (stopped) return;
    stopped = true;
    clearInterval(timer);
    source.disconnect();
    void ctx.close().catch(() => undefined);
  };

  const timer = setInterval(() => {
    analyser.getFloatTimeDomainData(frame);
    if (gate.push(rms(frame))) {
      stop();
      onVoice();
    }
  }, FRAME_MS);

  return stop;
}
